export default class OrderModel {
  observers = [];

  constructor(data) {
    if (data) {
      this.setData(data);
    }

    this.setIsDropShipping = this.setIsDropShipping.bind(this);
    this.setProductResellerPrice = this.setProductResellerPrice.bind(this);
    this.getData = this.getData.bind(this);
    this.subscribe = this.subscribe.bind(this);
    this.unsubscribe = this.unsubscribe.bind(this);
    this.notifySubscribers = this.notifySubscribers.bind(this);
    this.setTransportOption = this.setTransportOption.bind(this);
    this.setPaymentOption = this.setPaymentOption.bind(this);
    this.setDeliveryAddress = this.setDeliveryAddress.bind(this);
    this.setBillingAddress = this.setBillingAddress.bind(this);
    this.setItems = this.setItems.bind(this);
    this.setTransportValue = this.setTransportValue.bind(this);
    this.setOrderNotes = this.setOrderNotes.bind(this);
    this.setDeliveryAddressData = this.setDeliveryAddressData.bind(this);
    this.setClientOrderIdRef = this.setClientOrderIdRef.bind(this);
    this.setWarnings = this.setWarnings.bind(this);
  }

  /**
   * Order id
   * @type {string}
   */
  id;

  /**
   * Whether the order is of type dropshipping
   * @type {boolean}
   */
  dropShipment = false;

  /**
   * Specific type of dropshipping
   * @type {boolean}
   */
  dropShipmentType = null;

  /**
   * Set ds state
   * @param {boolean} state
   */
  setIsDropShipping(state) {
    this.dropShipment = state;
    this.notifySubscribers({ dropShipment: state });
  }

  notifySubscribers(data) {
    this.observers.forEach(observer => observer(data));
  }

  unsubscribe(fn) {
    const idx = this.observers.findIndex(f => f === fn);
    if (idx !== -1) {
      this.observers.splice(idx, 1);
    }
  }

  /**
   * Subscribe to notifications
   * @param {function} fn
   * @returns {function(*=): void} hook to unsubscribe
   */
  subscribe(fn) {
    this.unsubscribe(fn);

    this.observers.push(fn);

    return () => this.unsubscribe(fn);
  }

  /**
   * Set a reseller price for a product
   * @param {string} productId
   * @param {float} price
   */
  setProductResellerPrice(productId, price) {
    const idx = this.products.findIndex(p => p.id === productId);
    if (idx !== -1) {
      this.products[idx].resellerPrice = Math.abs(price);
      this.notifySubscribers({ products: this.products });
    }
  }

  /**
   * Load prototype
   * @param data {object}
   */
  setData(data = {}) {
    Object.entries(data || {}).forEach(([key, value]) => {
      switch (key) {
        case 'dropShipment':
          this[key] = !!value;
          if (typeof value === 'number') {
            this.dropShipmentType = value;
          }
          break;
        case 'dropShipmentType':
          if (value != null) {
            this.dropShipmentType = value;
          }
          break;
        case 'deliveryAddresses':
          if (value?.length) {
            if (data.dropShipment) {
              this.deliveryAddressData = {
                ...(this.deliveryAddressData || {}),
                ...(value.at(0) || value || {})
              };
            }
            this.deliveryAddress = value.at(0).id || value;
            this.hasDeliveryAddress = true;
          }
          break;
        case 'billingAddresses':
          if (value?.length) {
            this.billingAddress = value.at(0).id || value;
          }
          break;
        case 'billingDetails':
          if (value?.length) {
            this.billingDetails = value;
          }
          break;
        case 'deliveryAddressData':
          this.deliveryAddressData = {
            ...(this.deliveryAddressData || {}),
            ...(value || {})
          };
          break;
        case 'paymentOption':
        case 'transportOption':
          if (value?.id) {
            this[key] = value.id;
          } else {
            this[key] = value;
          }
          break;
        case 'cashOnDelivery':
          this.cashOnDelivery = !!value;
          break;
        case 'comment':
          if (value.length) {
            this.orderNotes = value;
          }
          break;
        case 'warnings':
          if (value?.length) {
            this.warnings = value || [];
          }
          break;
        case 'items':
          this.items = (value || []).map(item => {
            // if item contains code prop that means it's either from cart or from order
            if (item.code) {
              const payload = {
                // productId is in cart item, orderDetailId is in order item
                id: item.productId || item.orderDetailId || item.id,
                minQuantity: item.minQuantity,
                quantity: item.quantity,
                ...(!item.hasPrice && {
                  supplierId: item.supplierId,
                  supplierOfferId: item.supplierOfferId,
                })
              };

              if (
                data.dropShipment
                && data.id
                && 'cashOnDeliveryValue' in item
              ) {
                // if there is an order id and it's a dropShipment order
                // we need to use the cashOnDeliveryValue
                payload.cashOnDeliveryValue = item.cashOnDeliveryValue;
              }

              const promotionId = item.promotion?.promotionId
                || item.appliedPromotions?.at(0)?.promotionId;

              if (promotionId) {
                payload.appliedPromotions = [promotionId];
                if (item.promoItems) {
                  payload.promoItems = item.promoItems;
                }
              }

              if (payload.promoItems) {
                payload.promoItems = Object
                  .entries(item.promoItems)
                  .reduce((acc, [promoKey, promoItems]) => {
                    acc[promoKey] = promoItems.map(promoItem => {
                      const p = {
                        id: promoItem.productId || promoItem.orderDetailId || promoItem.id,
                        quantity: promoItem.quantity,
                        minQuantity: promoItem.minQuantity
                      };

                      if (
                        data?.dropShipment
                        && data.id
                        && 'cashOnDeliveryValue' in item
                      ) {
                        p.cashOnDeliveryValue = promoItem.cashOnDeliveryValue;
                      }
                      return p;
                    });
                    return acc;
                  }, {});
              }

              return payload;
            }

            return item;
          });
          break;
        default:
          this[key] = value;
          break;
      }
    });
  }

  /**
   * Transport option id
   * @type {string | object}
   */
  transportOption;

  /**
   * Set delivery address id
   * @param {string} transportOption
   */
  setTransportOption(transportOption) {
    this.transportOption = transportOption;
    this.notifySubscribers({ transportOption });
  }

  /**
   * Transport option
   * @type {string | object}
   */
  paymentOption;

  /**
   * Set delivery address id
   * @param {string} paymentOption
   */
  setPaymentOption(paymentOption) {
    this.paymentOption = paymentOption;
    this.notifySubscribers({ paymentOption });
  }

  /**
   * Delivery address id
   * @type {string|object}
   */
  deliveryAddress;

  /**
   * Delivery address id
   * @type {boolean}
   */
  hasDeliveryAddress;

  /**
   * Set delivery address id
   * @param {string} deliveryAddress
   */
  setDeliveryAddress(deliveryAddress) {
    this.deliveryAddress = deliveryAddress;
    this.notifySubscribers({ deliveryAddress });
  }

  /**
   * Cash on delivery (AWB ramburs)
   * @type {boolean}
   */
  cashOnDelivery = false;

  /**
   * Set cash on delivery value
   * @param {boolean} cashOnDelivery
   */
  setCashOnDelivery(cashOnDelivery) {
    this.cashOnDelivery = cashOnDelivery;
    this.notifySubscribers({ cashOnDelivery });
  }

  /**
   * Delivery address or details in case of dropShipping order
   * @type {object}
   */
  deliveryAddressData = {
    contactPersonName: null,
    contactPersonPhone: null,
    contactPersonRecipientEmail: null,
    country: null,
    county: null,
    city: null,
    address: null,
    postalCode: null,
    deliveryType: null,
    deliveryMode: null,
    deliveryLockerCode: null,
    deliveryLockerId: null,
  };

  /**
   * Sets a key for the delivery address
   * @param {string} key
   * @param {string|float}value
   */
  setDeliveryAddressData(key, value) {
    if (this.deliveryAddressData[key] === value) return;
    this.deliveryAddressData[key] = value;
    this.notifySubscribers({ deliveryAddressData: this.deliveryAddressData });
  }

  /**
   * Transport option id
   * @type {string}
   */
  billingAddress;

  /**
   * Set billing address
   * @param {string} billingAddress
   */
  setBillingAddress(billingAddress) {
    this.billingAddress = billingAddress;
    this.notifySubscribers({ billingAddress });
  }

  /**
   * Billing details
   * @type {string}
   */
  billingDetails;

  /**
   * Set billing details
   * @param {string} billingDetails
   */
  setBillingDetails(billingDetails) {
    if (this.billingDetails === billingDetails) return;
    this.billingDetails = billingDetails;
    this.notifySubscribers({ billingDetails });
  }

  /**
   * Warnings
   * @type {{ code: string, message: string }[]}
   */
  warnings;

  /**
    * Set warnings
    * @param {{ code: string, message: string }[]} warnings
    */
  setWarnings(warnings) {
    this.warnings = warnings;
    this.notifySubscribers({ warnings });
  }

  /**
   * Products
   * @type {{quantity: number, product_id: number, totalValue: number, promotions?: array}[]}
   */
  items = [];

  /**
   * Adds items
   * @param {{quantity: number, product_id: number, totalValue: number, promotions?: array}[]} items
   */
  setItems(items) {
    this.items = items.map(product => {
      const payload = {
        id: product.productId || product.orderDetailId || product.id,
        quantity: product.quantity,
        minQuantity: product.minQuantity,
        ...(!product.hasPrice && {
          supplierId: product.supplierId,
          supplierOfferId: product.supplierOfferId,
        })
      };
      if ('cashOnDeliveryValue' in product) {
        payload.cashOnDeliveryValue = product.cashOnDeliveryValue;
      }
      if (product.promotion) {
        payload.appliedPromotions = [product.promotion.promotionId];
        payload.promoItems = product.promoItems;
      }
      return payload;
    });
    this.notifySubscribers({ items: this.items });
  }

  /**
   * Update item if existing
   * @param item
   */
  updateItem(item) {
    const productId = item.orderDetailId || item.productId || item.id;
    const existingItemIdx = this.items.findIndex(i => {
      if (item.appliedPromotions) {
        return (
          productId === i.id
          && item.appliedPromotions.at(0) === i.appliedPromotions?.at(0)
        );
      }
      return (
        productId === i.id
        && (item?.supplierId === i.supplierId || (!item?.supplierId && !i.supplierId))
        && !i.appliedPromotions
      );
    });

    const existingItem = existingItemIdx >= 0 ? this.items[existingItemIdx] : {};

    const payload = {
      id: productId,
      ...existingItem,
      minQuantity: item.minQuantity || 1,
      quantity: item.quantity,
      ...(!item.hasPrice && {
        supplierId: item.supplierId,
        supplierOfferId: item.supplierOfferId,
      })
    };
    if ('cashOnDeliveryValue' in item) {
      payload.cashOnDeliveryValue = item.cashOnDeliveryValue;
    }

    const promotionId = item.promotion?.promotionId
      || item.promotionId
      || item.appliedPromotions?.at(0);

    if (promotionId) {
      payload.appliedPromotions = [promotionId];
      payload.promoItems = Object
        .entries(item.promoItems)
        .reduce((acc, [promoKey, promoItems]) => {
          acc[promoKey] = promoItems.map(promoItem => {
            const promoId = promoItem.orderDetailId || promoItem.productId || promoItem.id;
            const existingPromoItem = (existingItem.promoItems || {})[promoKey]
              ?.find(i => i.id === promoId);
            const p = {
              id: promoId,
              ...(existingPromoItem || {}),
              minQuantity: promoItem.minQuantity || 1,
              quantity: promoItem.quantity
            };
            if ('cashOnDeliveryValue' in promoItem) {
              p.cashOnDeliveryValue = promoItem.cashOnDeliveryValue;
            }
            return p;
          });
          return acc;
        }, {});
    }

    if (existingItemIdx !== -1) {
      this.items.splice(existingItemIdx, 1, payload);
    } else {
      this.items.push(payload);
    }
    this.notifySubscribers({ items: this.items });
  }

  /**
   * Add new item
   * @param {object} item
   */
  addItem(item) {
    const payload = {
      id: item.productId || item.orderDetailId || item.id,
      quantity: item.quantity,
      minQuantity: item.minQuantity,
      ...(!item.hasPrice && {
        supplierId: item.supplierId,
        supplierOfferId: item.supplierOfferId,
      })
    };

    if ('cashOnDeliveryValue' in item) {
      payload.cashOnDeliveryValue = item.cashOnDeliveryValue;
    }
    if (item.promotionId) {
      payload.appliedPromotions = [item.promotionId];
    } else if (item.promotion?.promotionId) {
      payload.appliedPromotions = [item.promotion.promotionId];
    }
    if (payload.appliedPromotions) {
      payload.promoItems = Object
        .entries(item.promoItems || {})
        .reduce((acc, [promoKey, promoItems]) => {
          acc[promoKey] = promoItems.map(promoItem => {
            const p = {
              id: promoItem.productId || promoItem.id,
              quantity: promoItem.quantity,
              minQuantity: promoItem.minQuantity,
            };
            if ('cashOnDeliveryValue' in promoItem) {
              p.cashOnDeliveryValue = promoItem.cashOnDeliveryValue;
            }
            return p;
          });
          return acc;
        }, {});
    }
    this.items.push(payload);
    this.notifySubscribers({ items: this.items });
  }

  /**
   * Removes item if exists
   * @param {object} item
   */
  removeItem(item) {
    const idxOfItem = this.items.indexOf(item);
    if (idxOfItem !== -1) {
      this.items.splice(idxOfItem, 1);
      this.notifySubscribers({ items: this.items });
    }
  }

  /**
   * Clear order items
   */
  removeItems() {
    this.items = [];
    this.notifySubscribers({ items: this.items });
  }

  /**
   * For dropShipping order we need to send the value inputted by the user
   * @type {float|number}
   */
  transportValue;

  /**
   * Sets transport value
   * @param {float|number|undefined} transportValue
   */
  setTransportValue(transportValue) {
    this.transportValue = transportValue;
    this.notifySubscribers({ transportValue });
  }

  /**
   * @type {string}
   */
  orderNotes;

  /**
   * Set order notes
   * @param {string} orderNotes
   */
  setOrderNotes(orderNotes) {
    this.orderNotes = orderNotes;
    this.notifySubscribers({ orderNotes });
  }

  /**
   * @type {string}
   */
  clientOrderIdRef;

  /**
   *
   * @param {string} clientOrderIdRef
   */
  setClientOrderIdRef(clientOrderIdRef) {
    if (this.clientOrderIdRef === clientOrderIdRef) return;
    this.clientOrderIdRef = clientOrderIdRef;
    this.notifySubscribers({ clientOrderIdRef });
  }

  /**
   * Get order totals
   * @returns {{}}
   */
  getTotals() {
    if (
      'totalValue' in this
      && 'vatValue' in this
    ) {
      return {
        total: this?.totalValue,
        totalVat: this?.vatValue,
        totalWithVat: this?.totalValueWithVat
      };
    }

    const total = this.items.reduce((acc, item) => acc + item.cashOnDeliveryValue, 0);
    const totalVat = total * 0.19;
    return {
      total,
      totalVat,
      totalWithVat: total + totalVat
    };
  }

  /**
   * Get order info
   * @returns object
   */
  getData() {
    const data = {
      cashOnDelivery: this.cashOnDelivery,
      transportOption: this.transportOption,
      paymentOption: this.paymentOption,
      billingAddress: this.billingAddress,
      billingDetails: this.billingDetails,
      dropShipment: this.dropShipment,
      dropShipmentType: this.dropShipmentType,
      items: this.items,
      deliveryAddress: this.deliveryAddress,
      deliveryAddressData: this.deliveryAddressData,
      transportValue: this.transportValue,
      orderNotes: this.orderNotes,
      warnings: this.warnings,
    };

    if (this.id) {
      data.id = this.id;
    }

    if (this.clientOrderIdRef) {
      data.clientOrderIdRef = this.clientOrderIdRef;
    }

    return data;
  }
}
